import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  School as SchoolIcon,
  LocationOn as LocationIcon,
  Group as TeamIcon,
  CheckCircle as CheckIcon,
  PlayArrow as ProcessIcon,
  MyLocation as MyLocationIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { auth } from '../../../../../firebase';
import { getApiBase } from '../../../../../utils/apiConfig';

// School years from 2017-2018 through 2025-2026 (based on user requirements)
const AVAILABLE_SCHOOL_YEARS = [
  '2017-2018', '2018-2019', '2019-2020', '2020-2021', 
  '2021-2022', '2022-2023', '2023-2024', '2024-2025', '2025-2026'
];

// Available grades K-12
const AVAILABLE_GRADES = [
  { id: 'KG', label: 'Kindergarten', shortLabel: 'K' },
  { id: '01', label: 'Grade 1', shortLabel: '1' },
  { id: '02', label: 'Grade 2', shortLabel: '2' },
  { id: '03', label: 'Grade 3', shortLabel: '3' },
  { id: '04', label: 'Grade 4', shortLabel: '4' },
  { id: '05', label: 'Grade 5', shortLabel: '5' },
  { id: '06', label: 'Grade 6', shortLabel: '6' },
  { id: '07', label: 'Grade 7', shortLabel: '7' },
  { id: '08', label: 'Grade 8', shortLabel: '8' },
  { id: '09', label: 'Grade 9', shortLabel: '9' },
  { id: '10', label: 'Grade 10', shortLabel: '10' },
  { id: '11', label: 'Grade 11', shortLabel: '11' },
  { id: '12', label: 'Grade 12', shortLabel: '12' }
];

const GRADE_ORDER = [
  'Pre-K', 'Kindergarten',
  'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6',
  'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
];

const NonNCESProcessingWorkflow = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Form state
  const [schoolData, setSchoolData] = useState({
    name: '',
    isCharter: false,
    address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    enrollmentData: {}, // Structure: { 'school-year': { 'grade': enrollment_count } }
    gradesOffered: [] // Array of grade IDs that the school offers
  });
  
  // Team selection state (separate like in EnhancedStandardProcessor)
  const [selectedTeam, setSelectedTeam] = useState('');
  
  // Processing state
  const [isValidating, setIsValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [processingResults, setProcessingResults] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Teams data
  const [availableTeams, setAvailableTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  
  // Enrollment management state
  const [selectedYear, setSelectedYear] = useState('2025-2026');
  const [enrollmentDialogOpen, setEnrollmentDialogOpen] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState({});
  
  // US States
  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  // Processing steps
  const steps = [
    'Geocoding',
    'ESRI Data',
    'Nearby Schools',
    'Projections',
    'Metrics',
    'AI Summaries',
    'Team Assignment'
  ];

  // Helper functions for API calls (copied from working EnhancedStandardProcessor)
  const getAuthHeaders = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      console.warn('No authenticated user found');
      return {};
    }
    try {
      const token = await firebaseUser.getIdToken();
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('Error getting auth token:', error);
      return {};
    }
  };

  const loadAvailableTeams = useCallback(async () => {
    setIsLoadingTeams(true);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${getApiBase()}/v2/admin/teams?getAllTeams=true`, {
        headers: authHeaders,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Teams API response:', data); // Debug logging
        
        // Deduplicate teams by ID
        const teams = data.teams || [];
        const uniqueTeams = teams.filter((team, index, self) => 
          index === self.findIndex(t => t.id === team.id)
        );
        
        setAvailableTeams(uniqueTeams);
      } else {
        console.error('Teams API error:', response.status, response.statusText);
        const errorData = await response.text();
        console.error('Error details:', errorData);
        enqueueSnackbar(`Failed to load teams: ${response.status} ${response.statusText}`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      enqueueSnackbar('Failed to load teams', { variant: 'error' });
    } finally {
      setIsLoadingTeams(false);
    }
  }, [enqueueSnackbar]);

  // Load teams on component mount
  useEffect(() => {
    loadAvailableTeams();
  }, [loadAvailableTeams]);

  // Validate address
  const validateAddress = async () => {
    if (!schoolData.name.trim()) {
      enqueueSnackbar('Please enter a school name', { variant: 'error' });
      return;
    }

    if (!schoolData.address.street.trim() || !schoolData.address.city.trim() || 
        !schoolData.address.state.trim() || !schoolData.address.zip.trim()) {
      enqueueSnackbar('Please fill in all address fields', { variant: 'error' });
      return;
    }

    setIsValidating(true);
    try {
      const addressString = `${schoolData.address.street}, ${schoolData.address.city}, ${schoolData.address.state} ${schoolData.address.zip}`;
      
      // Use same authentication method as teams loading
              const authHeaders = await getAuthHeaders();
        const response = await fetch(`${getApiBase()}/v2/schools/validate-address`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          address: addressString
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Address validation response:', data); // Debug logging
        setValidationResults(data);
        
        if (data.isValid) {
          enqueueSnackbar('Address validated successfully!', { variant: 'success' });
        } else {
          enqueueSnackbar(`Address validation warning: ${data.error || 'Please verify address'}`, { variant: 'warning' });
        }
      } else {
        const errorData = await response.text();
        console.error('Address validation error:', response.status, errorData);
        enqueueSnackbar(`Address validation failed: ${response.status}`, { variant: 'error' });
        setValidationResults({ isValid: false, error: `HTTP ${response.status}` });
      }
    } catch (error) {
      console.error('Address validation error:', error);
      enqueueSnackbar('Failed to validate address', { variant: 'error' });
      setValidationResults({ isValid: false, error: error.message });
    } finally {
      setIsValidating(false);
    }
  };

  // Process non-NCES school
  const processNonNCESSchool = async () => {
    if (!validationResults?.isValid) {
      enqueueSnackbar('Please validate the address first', { variant: 'error' });
      return;
    }

    if (!selectedTeam) {
      enqueueSnackbar('Please select a team', { variant: 'error' });
      return;
    }

    // Check if enrollment data is provided
    const hasEnrollmentData = Object.keys(schoolData.enrollmentData).some(year => 
      Object.keys(schoolData.enrollmentData[year] || {}).length > 0
    );

    if (!hasEnrollmentData) {
      enqueueSnackbar('Please add enrollment data for at least one school year', { variant: 'error' });
      return;
    }

    setIsProcessing(true);
    setCurrentStep(0);

    try {
      const requestBody = {
        name: schoolData.name.trim(),
        address: {
          street: schoolData.address.street.trim(),
          city: schoolData.address.city.trim(),
          state: schoolData.address.state.trim(),
          zip: schoolData.address.zip.trim()
        },
        team_id: selectedTeam,
        enrollment_data: schoolData.enrollmentData,
        grades_offered: schoolData.gradesOffered,
        processing_type: 'non_nces_processing'
      };

      // Simulate step-by-step processing
      const stepNames = ['geocoding', 'esri', 'nearby', 'projections', 'metrics', 'team'];
      
      for (let i = 0; i < stepNames.length; i++) {
        setCurrentStep(i);
        
        // Simulate processing time
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (i === stepNames.length - 1) {
          // Final step - actually process
          const authHeaders = await getAuthHeaders();
          const response = await fetch(`${getApiBase()}/v2/schools/new-with-enrollment`, {
            method: 'POST',
            headers: {
              ...authHeaders,
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(requestBody)
          });

          if (response.ok) {
            const data = await response.json();
            console.log('Processing response:', data); // Debug logging
            setProcessingResults(data);
          } else {
            const errorData = await response.text();
            console.error('Processing error:', response.status, errorData);
            throw new Error(`Processing failed: ${response.status} - ${errorData}`);
          }
        }
      }

      setCurrentStep(stepNames.length);
      enqueueSnackbar('Non-NCES school processed successfully!', { variant: 'success' });

    } catch (error) {
      console.error('Processing error:', error);
      enqueueSnackbar(`Processing failed: ${error.message}`, { variant: 'error' });
      setIsProcessing(false);
    }
  };

  // Handle enrollment data management
  const openEnrollmentDialog = (year) => {
    setSelectedYear(year);
    setEditingEnrollment(schoolData.enrollmentData[year] || {});
    setEnrollmentDialogOpen(true);
  };

  const saveEnrollmentData = () => {
    setSchoolData(prev => ({
      ...prev,
      enrollmentData: {
        ...prev.enrollmentData,
        [selectedYear]: { ...editingEnrollment }
      }
    }));
    setEnrollmentDialogOpen(false);
    enqueueSnackbar(`Enrollment data saved for ${selectedYear}`, { variant: 'success' });
  };

  const updateEnrollmentValue = (grade, value) => {
    setEditingEnrollment(prev => ({
      ...prev,
      [grade]: value === '' ? undefined : parseInt(value, 10)
    }));
  };

  const deleteEnrollmentYear = (year) => {
    setSchoolData(prev => ({
      ...prev,
      enrollmentData: {
        ...prev.enrollmentData,
        [year]: undefined
      }
    }));
    enqueueSnackbar(`Enrollment data deleted for ${year}`, { variant: 'success' });
  };

  // Reset form
  const resetForm = () => {
    setSchoolData({
      name: '',
      address: { street: '', city: '', state: '', zip: '' },
      enrollmentData: {},
      gradesOffered: []
    });
    setSelectedTeam('');
    setValidationResults(null);
    setProcessingResults(null);
    setCurrentStep(0);
    setIsProcessing(false);
  };

  // Get years with enrollment data
  const getYearsWithData = () => {
    return Object.keys(schoolData.enrollmentData).filter(year => 
      schoolData.enrollmentData[year] && Object.keys(schoolData.enrollmentData[year]).length > 0
    );
  };

  // Get team name from team ID
  const getTeamName = (teamId) => {
    const team = availableTeams.find(t => t.id === teamId);
    return team ? team.name : teamId;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <SchoolIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Non-NCES Processing
            </Typography>
            {isProcessing && (
              <Chip 
                label="Processing..." 
                color="primary" 
                icon={<CircularProgress size={16} />}
              />
            )}
          </Box>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Process schools without NCES IDs that have enrollment data. This workflow includes geocoding,
            ESRI demographic data collection, nearby schools analysis, enrollment projections, and team assignment.
          </Typography>

          <Alert severity="info">
            <AlertTitle>Processing Overview</AlertTitle>
            Schools will be processed through: Geocoding → ESRI Data → Nearby Schools → Projections → Metrics → AI Summaries → Team Assignment.
            <strong> Enrollment data from 2017-2018 through 2025-2026 can be entered for projection calculations.</strong>
          </Alert>
        </CardContent>
      </Card>

      {/* Processing Status */}
      {isProcessing && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Processing Progress
            </Typography>
            <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 2 }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <LinearProgress variant="determinate" value={(currentStep / steps.length) * 100} />
          </CardContent>
        </Card>
      )}

      {/* Success Results */}
      {processingResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CheckIcon color="success" sx={{ fontSize: 32 }} />
              <Typography variant="h5" color="success.main">
                Processing Complete!
              </Typography>
            </Box>
            
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>School Successfully Processed</AlertTitle>
              <strong>{schoolData.name}</strong> has been processed and added to team <strong>{getTeamName(selectedTeam)}</strong>.
            </Alert>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={resetForm}>
                Process Another School
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Main Form */}
      {!processingResults && (
        <>
          {/* School Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SchoolIcon />
                School Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="School Name"
                    value={schoolData.name}
                    onChange={(e) => setSchoolData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>School Type</InputLabel>
                    <Select
                      value={schoolData.isCharter ? 'charter' : 'district'}
                      label="School Type"
                      onChange={(e) => setSchoolData(prev => ({ 
                        ...prev, 
                        isCharter: e.target.value === 'charter' 
                      }))}
                    >
                      <MenuItem value="district">District School</MenuItem>
                      <MenuItem value="charter">Charter School</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Grades Offered
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select all grades that this school offers:
                  </Typography>
                  <FormGroup row>
                    {AVAILABLE_GRADES.map((grade) => (
                      <FormControlLabel
                        key={grade.id}
                        control={
                          <Checkbox
                            checked={schoolData.gradesOffered.includes(grade.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSchoolData(prev => ({
                                  ...prev,
                                  gradesOffered: [...prev.gradesOffered, grade.id]
                                }));
                              } else {
                                setSchoolData(prev => ({
                                  ...prev,
                                  gradesOffered: prev.gradesOffered.filter(g => g !== grade.id)
                                }));
                              }
                            }}
                          />
                        }
                        label={grade.shortLabel}
                        sx={{ minWidth: 80 }}
                      />
                    ))}
                  </FormGroup>
                  
                  {/* Quick select buttons */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSchoolData(prev => ({
                        ...prev,
                        gradesOffered: ['KG', '01', '02', '03', '04', '05']
                      }))}
                    >
                      Elementary (K-5)
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSchoolData(prev => ({
                        ...prev,
                        gradesOffered: ['06', '07', '08']
                      }))}
                    >
                      Middle (6-8)
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSchoolData(prev => ({
                        ...prev,
                        gradesOffered: ['09', '10', '11', '12']
                      }))}
                    >
                      High School (9-12)
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSchoolData(prev => ({
                        ...prev,
                        gradesOffered: AVAILABLE_GRADES.map(g => g.id)
                      }))}
                    >
                      All Grades (K-12)
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSchoolData(prev => ({
                        ...prev,
                        gradesOffered: []
                      }))}
                    >
                      Clear All
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon />
                Address Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    value={schoolData.address.street}
                    onChange={(e) => setSchoolData(prev => ({
                      ...prev,
                      address: { ...prev.address, street: e.target.value }
                    }))}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={schoolData.address.city}
                    onChange={(e) => setSchoolData(prev => ({
                      ...prev,
                      address: { ...prev.address, city: e.target.value }
                    }))}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth required>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={schoolData.address.state}
                      onChange={(e) => setSchoolData(prev => ({
                        ...prev,
                        address: { ...prev.address, state: e.target.value }
                      }))}
                      label="State"
                    >
                      {states.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={schoolData.address.zip}
                    onChange={(e) => setSchoolData(prev => ({
                      ...prev,
                      address: { ...prev.address, zip: e.target.value }
                    }))}
                    required
                  />
                </Grid>
              </Grid>

              {/* Validation Results */}
              {validationResults && (
                <Alert 
                  severity={validationResults.isValid ? 'success' : 'warning'} 
                  sx={{ mt: 2 }}
                >
                  {validationResults.isValid 
                    ? 'Address validation successful'
                    : `Address validation warning: ${validationResults.error || 'Please verify address'}`
                  }
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Enrollment Data */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon />
                Enrollment Data (2017-2018 through 2025-2026)
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add historical enrollment data by grade and school year. This data will be used for projections.
              </Typography>

              {/* Years with data */}
              {getYearsWithData().length > 0 && (
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>School Year</TableCell>
                        <TableCell>Total Enrollment</TableCell>
                        <TableCell>Grades</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getYearsWithData().map((year) => {
                        const yearData = schoolData.enrollmentData[year];
                        const totalEnrollment = Object.values(yearData).reduce((sum, val) => sum + (val || 0), 0);
                        const gradesWithData = Object.keys(yearData).filter(grade => yearData[grade] > 0);
                        
                        return (
                          <TableRow key={year}>
                            <TableCell>{year}</TableCell>
                            <TableCell>{totalEnrollment}</TableCell>
                            <TableCell>{gradesWithData.join(', ')}</TableCell>
                            <TableCell>
                              <IconButton 
                                size="small" 
                                onClick={() => openEnrollmentDialog(year)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => deleteEnrollmentYear(year)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Add enrollment data button */}
              <FormControl sx={{ minWidth: 200, mr: 2 }}>
                <InputLabel>Select School Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  label="Select School Year"
                >
                  {AVAILABLE_SCHOOL_YEARS.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => openEnrollmentDialog(selectedYear)}
              >
                Add Enrollment Data for {selectedYear}
              </Button>
            </CardContent>
          </Card>

          {/* Team Assignment */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TeamIcon />
                Team Assignment
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Select Team</InputLabel>
                    <Select
                      value={selectedTeam}
                      onChange={(e) => setSelectedTeam(e.target.value)}
                      label="Select Team"
                      disabled={isProcessing}
                    >
                      <MenuItem value="">
                        <em>No team assignment</em>
                      </MenuItem>
                      {isLoadingTeams ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Loading teams...
                        </MenuItem>
                      ) : availableTeams.length > 0 ? (
                        availableTeams.map((team) => (
                          <MenuItem key={team.id} value={team.id}>
                            <GroupIcon sx={{ mr: 1 }} />
                            {team.name} ({team.school_count} schools)
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          <em>No teams available</em>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}

      {/* Action Buttons */}
      {!processingResults && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={validateAddress}
                disabled={isValidating || isProcessing}
                startIcon={isValidating ? <CircularProgress size={20} /> : <MyLocationIcon />}
              >
                {isValidating ? 'Validating...' : 'Validate Address'}
              </Button>
              
              <Button
                variant="contained"
                onClick={processNonNCESSchool}
                disabled={!validationResults?.isValid || isProcessing}
                startIcon={isProcessing ? <CircularProgress size={20} /> : <ProcessIcon />}
                size="large"
              >
                {isProcessing ? 'Processing...' : 'Process Non-NCES School'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Enrollment Data Dialog */}
      <Dialog 
        open={enrollmentDialogOpen} 
        onClose={() => setEnrollmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Enrollment Data for {selectedYear}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {GRADE_ORDER.map((grade) => (
              <Grid item xs={6} sm={4} md={3} key={grade}>
                <TextField
                  fullWidth
                  label={grade}
                  type="number"
                  value={editingEnrollment[grade] || ''}
                  onChange={(e) => updateEnrollmentValue(grade, e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollmentDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveEnrollmentData} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NonNCESProcessingWorkflow;
